/**
 * AJANDA HESABI — ana ekranin "en yakin etkinlik" karti.
 *
 * `dashboard.ts` icinde `_calculateClosestUpcomingEvent` adiyla ozel bir
 * fonksiyondu ve HIC TESTI YOKTU. Saf bir hesap: veritabanina gitmiyor,
 * kendisine verilen grup/islem/kur listesinden uretiyor. Test edilemez
 * olmasinin tek sebebi disa acilmamis olmasiydi.
 *
 * Buraya BIREBIR tasindi; davranis degismedi. `agenda.characterization.test.ts`
 * civiliyor.
 */
import type { Group, DailyRate } from '@/types/database.types'
import type { AgendaWidgetData } from './types'
import { calculateNextPaymentDate, daysBetween } from './date'
import { formatNaturalDate } from './turkish'
import { calculatePotAmount, formatCurrency } from './finance'

export async function calculateClosestUpcomingEvent(
  groups: (Group & { member_count: number; total_cycles: number; next_member: any })[],
  currentUserId: string,
  rates: DailyRate | null,
  userTransactions: any[] = []
): Promise<AgendaWidgetData | null> {
  if (!groups || groups.length === 0) return null
  const activeGroups = groups.filter(
    g => g.status === 'active' && (g.current_cycle || 1) <= (g.total_cycles || g.member_count)
  )
  if (activeGroups.length === 0) return null

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  let closestGroup: any = null
  let minDays = Infinity
  let closestNextDate: Date | null = null

  for (const group of activeGroups) {
    const startDate = new Date(group.start_date)
    startDate.setHours(0, 0, 0, 0)
    const currentCycle = (group.current_cycle || 0) === 0 ? 1 : group.current_cycle || 1
    const cyclePeriod = (group.cycle_period as 'monthly' | 'weekly') || 'monthly'
    const totalCycles = group.total_cycles || group.member_count || 1

    // Kullanıcının bu döngüde ödeme durumunu kontrol et
    const nextMemberId = group.next_member?.id || null
    const isCurrentUserReceiver = nextMemberId === currentUserId

    // Gönderici olarak: bu döngüde confirmed/auto_confirmed ödeme var mı?
    const hasPaidThisCycle = !isCurrentUserReceiver && userTransactions.some(
      (t: any) => t.group_id === group.id && t.sender_id === currentUserId && t.cycle_number === currentCycle &&
        (t.status === 'confirmed' || t.status === 'auto_confirmed')
    )

    // Alıcı olarak: tüm üyelerden ödeme gelmiş mi?
    const members = (group as any).group_members || []
    const payerCount = members.filter((m: any) => m.turn_index !== currentCycle).length
    const confirmedPaymentsForReceiver = isCurrentUserReceiver
      ? userTransactions.filter(
          (t: any) => t.group_id === group.id && t.receiver_id === currentUserId && t.cycle_number === currentCycle &&
            (t.status === 'confirmed' || t.status === 'auto_confirmed')
        ).length
      : 0
    const allPaymentsReceived = isCurrentUserReceiver && confirmedPaymentsForReceiver >= payerCount && payerCount > 0

    // Eğer ödeme tamamlandıysa → bu döngüyü atla, varsa sonraki döngüyü hesapla
    if (hasPaidThisCycle || allPaymentsReceived) {
      // Sonraki döngü var mı?
      const nextCycle = currentCycle + 1
      if (nextCycle <= totalCycles) {
        const nextDate = calculateNextPaymentDate(startDate, nextCycle - 1, cyclePeriod)
        nextDate.setHours(0, 0, 0, 0)
        const daysLeft = daysBetween(today, nextDate)
        if (daysLeft >= 0 && daysLeft < minDays) {
          // Sonraki döngünün alıcısını bul
          const nextCycleMember = members.find((m: any) => m.turn_index === nextCycle)
          minDays = daysLeft
          closestGroup = { ...group, _overrideCycle: nextCycle, next_member: nextCycleMember?.users || group.next_member }
          closestNextDate = nextDate
        }
      }
      continue
    }

    const nextDate = calculateNextPaymentDate(startDate, currentCycle - 1, cyclePeriod)
    nextDate.setHours(0, 0, 0, 0)
    const daysLeft = daysBetween(today, nextDate)
    if (daysLeft >= 0 && daysLeft < minDays) {
      minDays = daysLeft
      closestGroup = group
      closestNextDate = nextDate
    }
  }

  if (!closestGroup || !closestNextDate) return null

  // Ay adları: Tam Türkçe, 5 karakterden uzunlar noktalı kısaltma
  const monthNames = [
    'OCAK',     // 4 ✓
    'ŞUBAT',    // 5 ✓
    'MART',     // 4 ✓
    'NİSAN',    // 5 ✓
    'MAYIS',    // 5 ✓
    'HAZ.',     // Haziran → 4
    'TEMM.',    // Temmuz → 5
    'AĞUS.',    // Ağustos → 5
    'EYLÜL',    // 5 ✓
    'EKİM',     // 4 ✓
    'KASIM',    // 5 ✓
    'ARA.',     // Aralık → 4
  ]
  // Gün adları: Kısa olanlar tam, uzunlar noktalı kısaltma
  const dayNames = [
    'Pazar',    // 5 ✓
    'Pzt.',     // Pazartesi → 4
    'Salı',     // 4 ✓
    'Çarş.',    // Çarşamba → 5
    'Perş.',    // Perşembe → 5
    'Cuma',     // 4 ✓
    'Cmt.',     // Cumartesi → 4
  ]
  const formattedMonth = monthNames[closestNextDate.getMonth()]
  const formattedDay = closestNextDate.getDate().toString()
  const dayName = dayNames[closestNextDate.getDay()]
  const nextMemberId = closestGroup.next_member?.id || null
  const isCurrentUserTurn = nextMemberId === currentUserId
  const nextMemberName = closestGroup.next_member?.full_name || 'Belirsiz'
  const memberCount = closestGroup.member_count || closestGroup.max_members || 1
  const groupAmount = closestGroup.amount || 0
  const groupCurrency = closestGroup.currency || 'TRY'
  const cyclePeriod = (closestGroup.cycle_period as 'monthly' | 'weekly') || 'monthly'

  const syncConvertToTRY = (amount: number, currency: string, ratesObj: DailyRate): number => {
    if (currency === 'TRY') return amount;
    switch (currency) {
      case 'USD': return Math.round(amount * ratesObj.usd_try * 100) / 100;
      case 'EUR': return Math.round(amount * ratesObj.eur_try * 100) / 100;
      case 'GRAM_GOLD':
      case 'GRAM_24_GOLD': return Math.round(amount * ratesObj.gram_24_try * 100) / 100;
      case 'GRAM_22_GOLD': return Math.round(amount * ratesObj.gram_22_try * 100) / 100;
      case 'QUARTER_GOLD': return Math.round(amount * (ratesObj.quarter_gold_try || ratesObj.gram_22_try * 1.754) * 100) / 100;
      case 'HALF_GOLD': return Math.round(amount * (ratesObj.half_gold_try || ratesObj.gram_22_try * 3.508) * 100) / 100;
      case 'FULL_GOLD': return Math.round(amount * (ratesObj.full_gold_try || ratesObj.gram_22_try * 7.016) * 100) / 100;
      case 'GRAM_SILVER': return Math.round(amount * (ratesObj.gram_silver_try || 0) * 100) / 100;
      default: return amount;
    }
  }

  let financialMain = ''
  let financialSub = ''

  if (isCurrentUserTurn) {
    const potAmount = calculatePotAmount(memberCount, groupAmount)
    financialMain = formatCurrency(potAmount, groupCurrency as any)
    if (rates && groupCurrency !== 'TRY') {
      try {
        const tryEquivalent = syncConvertToTRY(potAmount, groupCurrency, rates)
        financialSub = `≈ ${tryEquivalent.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} TL alacaksın`
      } catch {
        financialSub = ''
      }
    } else if (groupCurrency === 'TRY') {
      financialSub = 'alacaksın'
    }
  } else {
    financialMain = formatCurrency(groupAmount, groupCurrency as any)
    if (rates && groupCurrency !== 'TRY') {
      try {
        const tryEquivalent = syncConvertToTRY(groupAmount, groupCurrency, rates)
        financialSub = `≈ ${tryEquivalent.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} TL ödeyeceksin`
      } catch {
        financialSub = ''
      }
    } else if (groupCurrency === 'TRY') {
      financialSub = 'ödeyeceksin'
    }
  }

  return {
    groupName: closestGroup.name,
    groupId: closestGroup.id,
    daysRemaining: minDays,
    naturalDateText: formatNaturalDate(minDays, closestNextDate),
    formattedMonth,
    formattedDay,
    dayName,
    isCurrentUserTurn,
    nextMemberName,
    cyclePeriod,
    financialText: { main: financialMain, sub: financialSub },
  }
}
